import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { ContactForm, ContactFormSubmitted } from "./ContactForm";
import { streamChat, submitFeedback, trackEvent, type StreamEvent } from "./api-client";
import {
  clearSession,
  freshSession,
  loadSession,
  readPreviewCookie,
  saveSession,
  type SessionState,
  type WidgetMessage,
} from "./session-store";
import {
  CTA_COPY,
  resolveVariant,
  resolveInteractionVariant,
  type CtaVariant,
  type InteractionVariant,
} from "../lib/chat/experiment";
import { parseAssistantText, renderMarkdown } from "./formatters";
import { BenjiAvatar, SendIcon } from "./icons";

// Pool of cold-start suggestion pills. Four are picked at random each time the
// widget script loads, so repeat visitors see variety. Add new starters here as
// CCB learns more about which prospect intents convert — keep the pool ≥10 so
// rotation feels meaningful.
const STARTER_POOL = [
  // Corporate / B2B
  "Planning a company event — what do you need from me?",
  "We need a coffee bar for an office activation",
  "It's for a corporate team event",
  // Wedding
  "It's for a wedding — do you do full-day setups?",
  // Social
  "Around 100 guests at a birthday party — what would that run?",
  "We're throwing a graduation party",
  // Exploring / qualifying
  "What's included in a typical setup?",
  "How far do you travel for events?",
  "What's the minimum for a smaller gathering?",
  // Scale / urgency
  "We've got 200+ guests — can you handle that?",
  "We need something in the next few weeks",
  // Niche
  "It's for a film or TV production",
];

// Pick 4 starters at random each widget load for variety.
const COLD_START_SUGGESTIONS = [...STARTER_POOL]
  .sort(() => Math.random() - 0.5)
  .slice(0, 4);

type View = "chat" | "form" | "submitted";

export function BenjiApp() {
  const [session, setSession] = useState<SessionState>(() => loadSession());
  const [view, setView] = useState<View>("chat");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [submittedName, setSubmittedName] = useState<string>("");
  const [submittedEmail, setSubmittedEmail] = useState<string>("");
  // Tracks which `picks` chips the prospect has tapped per assistant message.
  // Per-message so the same label across two different replies (e.g. "Indoors"
  // asked twice) doesn't bleed state. Cleared on send.
  const [pickSelections, setPickSelections] = useState<Record<string, string[]>>({});
  const abortRef = useRef<AbortController | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  // Tracks the assistant bubble currently receiving text_deltas. Reassigned
  // when the server emits `tool_pending` or `message_break` to split a turn
  // into multiple bubbles. Reset to null when the stream completes/aborts.
  const currentBubbleRef = useRef<string | null>(null);
  // Minimum-display hold for the typing-dots bubble after `tool_pending`.
  // Sean's spec: prospects should see "Benji is thinking" for at least 3
  // seconds so the typing indicator registers visually even when the tool
  // returns instantly. text_delta / message_break / message_done events that
  // arrive during the hold are queued and replayed when the timer fires.
  const TYPING_HOLD_MS = 3000;
  const typingHoldUntilRef = useRef<number>(0);
  const heldQueueRef = useRef<StreamEvent[]>([]);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist session changes to localStorage.
  useEffect(() => saveSession(session), [session]);

  // Lock in the visitor's A/B variants on first render and fire the "shown"
  // funnel event. Two orthogonal experiments run concurrently:
  //   - variant (A–F): CTA copy
  //   - interactionVariant (A–C): launcher microinteraction
  // Each resolves independently (different hash salts inside the lib). If
  // WP forces a specific letter via window.__BENJI__, use it; if "auto" or
  // missing, hash the sessionId for stable assignment.
  const configCta = (window.__BENJI__?.ctaVariant ?? "A") as string;
  const variant: CtaVariant = session.experiment.variant
    ? (session.experiment.variant as CtaVariant)
    : resolveVariant(configCta, session.sessionId);

  const configInteraction = (window.__BENJI__?.interactionVariant ?? "auto") as string;
  const interactionVariant: InteractionVariant = session.experiment.interactionVariant
    ? (session.experiment.interactionVariant as InteractionVariant)
    : resolveInteractionVariant(configInteraction, session.sessionId);

  useEffect(() => {
    if (!session.experiment.variant || !session.experiment.interactionVariant) {
      setSession((s) => ({
        ...s,
        experiment: { ...s.experiment, variant, interactionVariant },
      }));
    }
    if (!session.experiment.trackedShown) {
      trackEvent(session.sessionId, variant, interactionVariant, "shown");
      setSession((s) => ({
        ...s,
        experiment: { ...s.experiment, trackedShown: true },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cold start — drop in the greeting + suggestion pills the first time the
  // user opens the window. We render the suggestions inline (pseudo-message)
  // so the conversation feels natural.
  useEffect(() => {
    if (session.uiOpen && session.messages.length === 0) {
      const greeting: WidgetMessage = {
        id: `greet-${Date.now()}`,
        role: "assistant",
        text: "Hey! I'm Benji ☕\nTell me about your event and I'll get you real pricing in under a minute.",
      };
      setSession((s) => ({ ...s, messages: [greeting] }));
    }
  }, [session.uiOpen, session.messages.length]);

  // Auto-scroll thread on new content.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [session.messages, streaming, view]);

  function openWindow() {
    setSession((s) => ({
      ...s,
      uiOpen: true,
      experiment: { ...s.experiment, trackedOpened: true },
    }));
    if (!session.experiment.trackedOpened) {
      trackEvent(session.sessionId, variant, interactionVariant, "opened");
    }
  }
  function closeWindow() {
    setSession((s) => ({ ...s, uiOpen: false }));
    abortRef.current?.abort();
  }

  // Detect the team's preview cookie once per mount. Used to gate the "New
  // chat" header button — only the team should see a hard-reset affordance,
  // not real prospects mid-conversation.
  const isPreviewMode = useMemo(() => readPreviewCookie().length > 0, []);

  /**
   * Toggle a `picks` chip — adds the label to the composer if it isn't there,
   * removes it if it is. Keeps a per-message set so the chip's active state
   * reflects what's currently in the composer. Multi-select chips don't auto-
   * send; the user composes a multi-answer reply, optionally types more, then
   * hits Send like a normal message.
   */
  function togglePick(messageId: string, label: string) {
    const current = pickSelections[messageId] ?? [];
    const isOn = current.includes(label);
    const next = isOn ? current.filter((c) => c !== label) : [...current, label];
    setPickSelections((s) => ({ ...s, [messageId]: next }));

    setInput((prev) => {
      if (isOn) {
        // Remove the label. Try comma-separated forms first to avoid leaving
        // dangling ", ". Falls back to a plain replace if the user has edited.
        const patterns = [`, ${label}`, `${label}, `, label];
        for (const p of patterns) {
          if (prev.includes(p)) return prev.replace(p, "");
        }
        return prev;
      }
      // Add — comma-separated if there's already content.
      const trimmed = prev.trimEnd();
      if (!trimmed) return label;
      // If the existing text ends mid-sentence, separate with ", "; if it
      // already ends in a comma, just append a space + label.
      if (trimmed.endsWith(",")) return `${trimmed} ${label}`;
      return `${trimmed}, ${label}`;
    });
  }

  function startFreshChat() {
    abortRef.current?.abort();
    clearSession();
    const next = freshSession();
    setSession(next);
    setView("chat");
    setInput("");
    setStreamingId(null);
    setStreaming(false);
    setSubmittedName("");
    setSubmittedEmail("");
    setPickSelections({});
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    typingHoldUntilRef.current = 0;
    heldQueueRef.current = [];
  }

  // `?ccb-benji-reset=1` magic-link trigger. Strips itself from the URL after
  // firing so a refresh doesn't keep wiping state. Runs once on mount.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("ccb-benji-reset") === "1") {
      url.searchParams.delete("ccb-benji-reset");
      window.history.replaceState({}, "", url.toString());
      startFreshChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSend(text: string) {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    // Clear any leftover typing-hold state from the previous turn. Without
    // this, a stale held event could replay into THIS turn's bubble.
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    typingHoldUntilRef.current = 0;
    heldQueueRef.current = [];
    currentBubbleRef.current = null;

    // Funnel step 3 — first user message of this session is the strongest
    // engagement signal short of capture. Don't refire on subsequent turns.
    if (!session.experiment.trackedMessaged) {
      trackEvent(session.sessionId, variant, interactionVariant, "messaged");
      setSession((s) => ({
        ...s,
        experiment: { ...s.experiment, trackedMessaged: true },
      }));
    }

    const userMsg: WidgetMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      text: trimmed,
    };
    const placeholderId = `a-${Date.now()}`;
    const placeholder: WidgetMessage = {
      id: placeholderId,
      role: "assistant",
      text: "",
      streaming: true,
    };
    setSession((s) => ({ ...s, messages: [...s.messages, userMsg, placeholder] }));
    setInput("");
    // Reset the auto-grown textarea height back to its single-row default —
    // setting input="" alone doesn't shrink it, only the inline height does.
    const composerTa = document.querySelector(".ccb-bx-composer textarea") as HTMLTextAreaElement | null;
    if (composerTa) composerTa.style.height = "auto";
    setPickSelections({}); // reset every chip's active state — they're for the prior turn
    setStreaming(true);
    setStreamingId(placeholderId);
    // Natural-pacing: track the currently-active bubble id. `tool_pending` and
    // `message_break` events split the assistant's turn into multiple bubbles;
    // text_delta always appends to whichever bubble is current. We use a ref
    // because handlers below close over a stale value otherwise.
    currentBubbleRef.current = placeholderId;

    try {
      await streamChat(
        { sessionId: session.sessionId, threadId: session.threadId, message: trimmed },
        (evt) => handleStreamEvent(evt, placeholderId),
        ac.signal,
      );
    } catch (err) {
      if ((err as Error)?.name !== "AbortError") {
        appendError(currentBubbleRef.current ?? placeholderId, (err as Error)?.message ?? "Something went wrong");
      }
    } finally {
      setStreaming(false);
      setStreamingId(null);
      // Intentionally NOT clearing currentBubbleRef or the typing-hold state
      // here — the server's last text_delta + message_done may still be sitting
      // in the held-events queue waiting for the 3s typing-bubble timer to
      // fire. Clearing too early would orphan those events and the post-tool
      // answer would never reach the bubble. The next handleSend resets all
      // of it at the start of the next turn instead.
    }
  }

  /**
   * Promote the current bubble out of "streaming" state and open a fresh
   * empty bubble that will show a typing indicator until the next text_delta
   * arrives. Used for both `tool_pending` (server is about to run a tool)
   * and `message_break` (model wants to chunk).
   */
  function openNewBubble() {
    const newId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const prevId = currentBubbleRef.current;
    let reusedExistingEmpty = false;
    setSession((s) => {
      // If the previous bubble is still empty (typing-dots only, no text has
      // arrived yet), REUSE it instead of stacking another one. Without this,
      // two back-to-back tool_pending events (e.g. model chains check_service_
      // area then get_instant_quote) would leave an orphan empty bubble in
      // the thread that just shows dots forever.
      if (prevId) {
        const prev = s.messages.find((m) => m.id === prevId);
        if (prev && prev.role === "assistant" && !prev.text) {
          reusedExistingEmpty = true;
          // Make sure streaming flag is on so the dots animation stays alive.
          return {
            ...s,
            messages: s.messages.map((m) =>
              m.id === prevId ? { ...m, streaming: true } : m,
            ),
          };
        }
      }
      // Otherwise: finalize the previous bubble (had text → it's a real
      // reply now, stop streaming) and append a fresh typing bubble.
      const cleared = prevId
        ? s.messages.map((m) => (m.id === prevId ? { ...m, streaming: false } : m))
        : s.messages;
      const newBubble: WidgetMessage = {
        id: newId,
        role: "assistant",
        text: "",
        streaming: true,
      };
      return { ...s, messages: [...cleared, newBubble] };
    });
    // Mutate the ref AFTER setSession runs so the branch decision is final.
    if (reusedExistingEmpty) {
      // currentBubbleRef stays as prevId; streamingId already points there.
      if (prevId) setStreamingId(prevId);
    } else {
      currentBubbleRef.current = newId;
      setStreamingId(newId);
    }
  }

  /** Events that mutate visible bubble state — these get queued during the
   *  post-tool typing hold so the dots stay visible for the full duration.
   *  tool_call/tool_result/progress have no UI effect; thread + error fire
   *  immediately regardless. */
  function shouldHoldDuringTypingPause(type: string): boolean {
    return type === "text_delta" || type === "message_break" || type === "message_done";
  }

  function drainHeldEvents(initialPlaceholderId: string) {
    typingHoldUntilRef.current = 0;
    holdTimerRef.current = null;
    const queue = heldQueueRef.current;
    heldQueueRef.current = [];
    for (const evt of queue) {
      // Recursive call — hold has cleared, so each event flows through the
      // switch normally.
      handleStreamEvent(evt, initialPlaceholderId);
    }
  }

  function scheduleDrain(initialPlaceholderId: string) {
    if (holdTimerRef.current) return; // already scheduled
    const wait = Math.max(0, typingHoldUntilRef.current - Date.now());
    holdTimerRef.current = setTimeout(() => drainHeldEvents(initialPlaceholderId), wait);
  }

  function handleStreamEvent(evt: StreamEvent, initialPlaceholderId: string) {
    // If we're inside the post-tool typing hold AND this event would mutate
    // the visible bubble, queue it for replay when the hold expires.
    if (
      shouldHoldDuringTypingPause(evt.type) &&
      Date.now() < typingHoldUntilRef.current
    ) {
      heldQueueRef.current.push(evt);
      scheduleDrain(initialPlaceholderId);
      return;
    }

    switch (evt.type) {
      case "thread":
        setSession((s) => ({ ...s, threadId: (evt.threadId as string) ?? s.threadId }));
        break;
      case "text_delta": {
        const targetId = currentBubbleRef.current ?? initialPlaceholderId;
        setSession((s) => ({
          ...s,
          messages: s.messages.map((m) =>
            m.id === targetId ? { ...m, text: m.text + (evt.text as string) } : m,
          ),
        }));
        break;
      }
      case "tool_pending":
        // Server is about to run a tool — close the current bubble (likely
        // contains the "let me check" announce text) and open a typing-dots
        // bubble so the prospect sees the bot is working.
        openNewBubble();
        // Start the minimum-display hold so the dots stay visible even if the
        // tool returns instantly. Reset on subsequent tool_pending events so
        // sequential tool iterations each get the full 3s.
        typingHoldUntilRef.current = Date.now() + TYPING_HOLD_MS;
        scheduleDrain(initialPlaceholderId);
        break;
      case "message_break":
        // Model emitted [[break]] OR the server is starting a post-tool
        // continuation. Either way, split the bubble.
        openNewBubble();
        break;
      case "tool_call":
      case "tool_result":
      case "progress":
        // Don't surface tool details or heartbeats to the prospect.
        break;
      case "message_done": {
        // Promote the LAST bubble's local id to the real DB id so feedback
        // PATCHes have a valid target. Earlier bubbles in the turn keep
        // their local ids (feedback row hidden by !startsWith("a-") gate).
        const realId = (evt.messageId as string) ?? null;
        const lastId = currentBubbleRef.current ?? initialPlaceholderId;
        setSession((s) => ({
          ...s,
          messages: s.messages.map((m) =>
            m.id === lastId
              ? { ...m, streaming: false, id: realId ?? m.id }
              : m,
          ),
        }));
        // Drop the global "this id is the streaming bubble" pointer so any
        // bubbles whose text never landed stop rendering the typing-dots
        // indicator. Paired with the empty-bubble hide rule in MessageRow,
        // an orphan typing bubble disappears as soon as the stream ends.
        setStreamingId(null);
        break;
      }
      case "error":
        appendError(currentBubbleRef.current ?? initialPlaceholderId, (evt.message as string) ?? "Sorry — something went wrong.");
        break;
    }
  }

  async function handleVote(messageId: string, rating: "up" | "down") {
    // Skip optimistic state for local placeholder ids — the server has no row
    // for them yet, so the PATCH would 404. The thumbs row isn't rendered
    // in that case anyway (see MessageRow guard).
    if (messageId.startsWith("a-") || messageId.startsWith("greet-")) return;

    const current = session.messages.find((m) => m.id === messageId)?.feedback ?? null;
    const optimistic = current === rating ? null : rating;
    setSession((s) => ({
      ...s,
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, feedback: optimistic } : m)),
    }));
    const result = await submitFeedback(messageId, rating, session.sessionId);
    setSession((s) => ({
      ...s,
      messages: s.messages.map((m) =>
        m.id === messageId ? { ...m, feedback: result.ok ? (result.value ?? null) : current } : m,
      ),
    }));
  }

  function appendError(placeholderId: string, message: string) {
    setSession((s) => ({
      ...s,
      messages: s.messages.map((m) =>
        m.id === placeholderId ? { ...m, streaming: false, text: `⚠ ${message}` } : m,
      ),
    }));
  }

  function openContactForm() {
    setView("form");
  }

  function handleSubmitted(_leadId: string) {
    setView("submitted");
    setSession((s) => ({
      ...s,
      capturedLeadId: _leadId,
      experiment: { ...s.experiment, trackedCaptured: true },
    }));
    if (!session.experiment.trackedCaptured) {
      trackEvent(session.sessionId, variant, interactionVariant, "captured");
    }
  }

  function keepChatting() {
    setView("chat");
  }

  // Use the locked-in per-session variant (resolved at top of component).
  const ctaCopy = CTA_COPY[variant];

  const showSuggestions = useMemo(
    () => view === "chat" && session.messages.length <= 1 && !streaming,
    [view, session.messages.length, streaming],
  );

  if (!session.uiOpen) {
    return <Launcher copy={ctaCopy} interactionVariant={interactionVariant} onOpen={openWindow} />;
  }

  return (
    <div className="ccb-bx-window" role="dialog" aria-label="Chat with Benji">
      <header className="ccb-bx-header">
        <div className="ccb-bx-header__avatar">
          <BenjiAvatar size={32} color="var(--ccb-bx-black)" />
        </div>
        <div className="ccb-bx-header__meta">
          <div className="ccb-bx-header__name">Benji</div>
          <div className="ccb-bx-header__sub">
            <span className="ccb-bx-header__pip" />
            Coffee Cart Boys · replies in seconds
          </div>
        </div>
        {isPreviewMode && (
          <button
            type="button"
            onClick={() => {
              if (
                session.messages.length === 0 ||
                window.confirm("Start a fresh chat? This conversation is already saved in the dashboard.")
              ) {
                startFreshChat();
              }
            }}
            className="ccb-bx-header__newchat"
            aria-label="Start a new chat (team only)"
            title="Start a new chat — team only"
          >
            ↻ New
          </button>
        )}
        <button
          type="button"
          onClick={closeWindow}
          className="ccb-bx-header__close"
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <div ref={threadRef} className="ccb-bx-thread">
        {session.messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            streamingId={streamingId}
            onVote={handleVote}
            onChipClick={(label) => handleSend(label)}
            picksActive={pickSelections[m.id] ?? []}
            onPickToggle={(label) => togglePick(m.id, label)}
          />
        ))}

        {showSuggestions && (
          <div className="ccb-bx-suggestions">
            <div className="ccb-bx-suggestions__lbl">↳ Tap to send</div>
            {COLD_START_SUGGESTIONS.map((s) => (
              <button key={s} type="button" onClick={() => handleSend(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {view === "form" && (
          <ContactForm session={session} onSubmitted={(id) => {
            // Capture name/email for the submitted card.
            const lastUser = session.messages
              .slice()
              .reverse()
              .find((m) => m.role === "user");
            setSubmittedName(lastUser?.text ?? "");
            handleSubmitted(id);
          }} />
        )}

        {view === "submitted" && (
          <ContactFormSubmitted
            name={submittedName || "friend"}
            email={submittedEmail || ""}
            eventDate={session.qualifying.eventDate}
            onKeepChatting={keepChatting}
          />
        )}
      </div>

      {view === "chat" && (
        <>
          <div className="ccb-bx-composer">
            <textarea
              value={input}
              onInput={(e) => {
                const el = e.target as HTMLTextAreaElement;
                setInput(el.value);
                // Auto-grow: shrink to 1-row to measure, then expand to fit
                // content up to max-height (capped via CSS). Has to happen
                // here, not via CSS — textarea height isn't content-driven
                // without scripting.
                el.style.height = "auto";
                el.style.height = Math.min(el.scrollHeight, 140) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(input);
                }
              }}
              rows={1}
              placeholder="Type a message…"
              disabled={streaming}
            />
            <button
              type="button"
              className="ccb-bx-composer__send"
              onClick={() => handleSend(input)}
              disabled={streaming || !input.trim()}
              aria-label="Send"
            >
              <SendIcon size={20} />
            </button>
          </div>
          {/* Surface contact-form CTA after a few exchanges. Once captured, hide. */}
          {session.messages.length >= 4 && !session.capturedLeadId && (
            <div style={{ textAlign: "center", padding: "0 14px 6px" }}>
              <a
                href="https://coffeecartboys.com/catering-inquiries/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => saveSession({ ...session, uiOpen: false })}
                style={{
                  color: "#B57200",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                }}
              >
                Skip to quote request →
              </a>
            </div>
          )}
        </>
      )}

      <div className="ccb-bx-footer">
        Powered by Coffee Cart Boys · we'll never share your info
      </div>
    </div>
  );

  // Stops eslint-no-unused: we read submittedEmail in ContactFormSubmitted indirectly.
  void setSubmittedEmail;
}

function MessageRow({
  message,
  streamingId,
  onVote,
  onChipClick,
  picksActive,
  onPickToggle,
}: {
  message: WidgetMessage;
  streamingId: string | null;
  onVote: (messageId: string, rating: "up" | "down") => void;
  onChipClick: (label: string) => void;
  picksActive: string[];
  onPickToggle: (label: string) => void;
}) {
  if (message.role === "user") {
    return <div className="ccb-bx-user">{message.text}</div>;
  }
  if (message.role === "system_event") {
    return null;
  }
  const isStreaming = message.id === streamingId && (message.text === "" || message.streaming);
  // Hide assistant bubbles that have no text AND are no longer the active
  // streaming target. These are orphans from a tool-pending sequence that
  // never produced visible text (e.g. the model called two tools back-to-back
  // and the first typing bubble got skipped). Without this rule the empty
  // dots bubble hangs in the thread after message_done.
  if (!message.text && !isStreaming) return null;
  // Don't render thumbs for the greeting (`greet-…`) or pre-message_done
  // placeholders (`a-…`) — there's no DB row for those.
  const isRated = message.id && !message.id.startsWith("a-") && !message.id.startsWith("greet-");
  const showThumbs = !isStreaming && message.text && isRated;
  // Parse markdown + ui-hint groups. Groups only render once streaming has
  // finished — otherwise we'd flash a half-formed `[[chips:...]]` line during
  // the partial reply and trigger handlers on an incomplete chip set.
  const parsed = message.text
    ? parseAssistantText(message.text)
    : { text: "", groups: [] };
  const displayText = isStreaming ? message.text : parsed.text;
  const groups = isStreaming ? [] : parsed.groups;
  return (
    <div className="ccb-bx-bot">
      <div className="ccb-bx-bot__avatar">
        <BenjiAvatar size={23} color="var(--ccb-bx-black)" />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          className={
            "ccb-bx-bot__bubble" + (displayText ? "" : " ccb-bx-bot__bubble--typing")
          }
        >
          {/* iMessage-style: when this bubble is the active streaming bubble
              and hasn't received any text yet, render the bouncing-dots typing
              indicator. Once even one character lands, the dots disappear and
              text streams in naturally. No trailing cursor — text alone is
              the cue that the bot is still writing. */}
          {displayText ? renderMarkdown(displayText) : <Typing />}
        </div>
        {groups.map((g, gi) => {
          if (g.kind === "chips") {
            return (
              <div key={`${message.id}-g-${gi}`} className="ccb-bx-chips">
                {g.labels.map((c, i) => (
                  <button
                    key={`${message.id}-c-${gi}-${i}`}
                    type="button"
                    onClick={() => onChipClick(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            );
          }
          if (g.kind === "date") {
            return (
              <DatePicker
                key={`${message.id}-g-${gi}`}
                onPick={(iso) => onChipClick(formatDateForChat(iso))}
              />
            );
          }
          if (g.kind === "time") {
            return (
              <TimePicker
                key={`${message.id}-g-${gi}`}
                onPick={(t) => onChipClick(t)}
              />
            );
          }
          // picks: multi-select; tap toggles into composer
          return (
            <div key={`${message.id}-g-${gi}`} className="ccb-bx-picks">
              <div className="ccb-bx-picks__lbl">Pick any that apply</div>
              <div className="ccb-bx-picks__row">
                {g.labels.map((c, i) => {
                  const active = picksActive.includes(c);
                  return (
                    <button
                      key={`${message.id}-p-${gi}-${i}`}
                      type="button"
                      onClick={() => onPickToggle(c)}
                      className={active ? "is-active" : ""}
                      aria-pressed={active}
                    >
                      {active ? "✓ " : ""}{c}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {showThumbs && (
          <ThumbsRow
            current={message.feedback ?? null}
            onUp={() => onVote(message.id, "up")}
            onDown={() => onVote(message.id, "down")}
          />
        )}
      </div>
    </div>
  );
}

function ThumbsRow({
  current,
  onUp,
  onDown,
}: {
  current: "up" | "down" | null;
  onUp: () => void;
  onDown: () => void;
}) {
  return (
    <div className="ccb-bx-thumbs">
      <button
        type="button"
        onClick={onUp}
        className={current === "up" ? "active" : ""}
        aria-label="Helpful"
        title={current === "up" ? "Helpful (click to clear)" : "Helpful"}
      >
        <ThumbsUpIcon filled={current === "up"} />
      </button>
      <button
        type="button"
        onClick={onDown}
        className={current === "down" ? "active" : ""}
        aria-label="Not helpful"
        title={current === "down" ? "Not helpful (click to clear)" : "Not helpful"}
      >
        <ThumbsDownIcon filled={current === "down"} />
      </button>
    </div>
  );
}

// Lucide-style stroke icons inlined as SVG. Stroke uses currentColor so brand
// brown / gold can be controlled from CSS. Filled state pairs `fill` with the
// outline for an obvious selected look — the emoji 👍 / 👎 disappear against
// our cream background, so this is a hard contrast win.
function ThumbsUpIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7v-12l4-9 1.34 0a2 2 0 0 1 1.66 2.88Z" />
    </svg>
  );
}

function ThumbsDownIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17v12l-4 9-1.34 0a2 2 0 0 1-1.66-2.88Z" />
    </svg>
  );
}

function Typing() {
  return (
    <span className="ccb-bx-typing">
      <span /> <span /> <span />
    </span>
  );
}

/**
 * Inline date picker rendered when Benji emits `[[date]]`. Uses the native
 * `<input type="date">` because:
 *   1. iOS Safari/Android Chrome render a beautiful OS-native wheel for it.
 *   2. Zero custom calendar code to maintain or accessibility-audit.
 *   3. `min` attribute auto-blocks past dates without any JS gymnastics.
 *
 * On pick, we hand the selected ISO date up to the parent's onChipClick
 * handler (after formatting to a human string) which routes through the
 * same handleSend path as a chip tap.
 */
function DatePicker({ onPick }: { onPick: (iso: string) => void }) {
  const [value, setValue] = useState<string>("");
  const todayIso = new Date().toISOString().slice(0, 10);

  function commit() {
    if (!value) return;
    onPick(value);
    setValue("");
  }

  return (
    <div className="ccb-bx-datepicker">
      <label className="ccb-bx-datepicker__lbl">Pick your event date</label>
      <div className="ccb-bx-datepicker__row">
        <div className="ccb-bx-picker__input-wrap">
          {!value && <span className="ccb-bx-picker__placeholder">Select a date</span>}
          <input
            type="date"
            value={value}
            min={todayIso}
            onChange={(e) => setValue((e.target as HTMLInputElement).value)}
            aria-label="Event date"
          />
        </div>
        <button
          type="button"
          className="ccb-bx-datepicker__send"
          disabled={!value}
          onClick={commit}
        >
          Use date
        </button>
      </div>
    </div>
  );
}

function TimePicker({ onPick }: { onPick: (display: string) => void }) {
  const [value, setValue] = useState<string>("");

  function commit() {
    if (!value) return;
    onPick(formatTimeForChat(value));
    setValue("");
  }

  return (
    <div className="ccb-bx-datepicker ccb-bx-timepicker">
      <label className="ccb-bx-datepicker__lbl">Pick your start time</label>
      <div className="ccb-bx-datepicker__row">
        <div className="ccb-bx-picker__input-wrap">
          {!value && <span className="ccb-bx-picker__placeholder">Select a time</span>}
          <input
            type="time"
            value={value}
            onChange={(e) => setValue((e.target as HTMLInputElement).value)}
            aria-label="Event start time"
          />
        </div>
        <button
          type="button"
          className="ccb-bx-datepicker__send"
          disabled={!value}
          onClick={commit}
        >
          Use time
        </button>
      </div>
    </div>
  );
}

/** Turn "14:00" into "2:00 PM" for display in the chat thread. */
function formatTimeForChat(hhmm: string): string {
  try {
    const [h, m] = hhmm.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
  } catch {
    return hhmm;
  }
}

/** Turn 2026-06-15 into "June 15, 2026" so what the prospect sees in the
 *  message thread reads naturally, not as an ISO date. */
function formatDateForChat(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

// Variant C — rotating speech-bubble teaser. Cycles through these every
// ~10s in place of the static CTA copy. JS-driven because pure CSS can't
// cycle text content.
const VARIANT_C_TEASERS = [
  "Need a quote?",
  "Got an event?",
  "Ask me anything",
  "Custom drinks?",
] as const;

// Variant B — first-mount accelerated wiggle. The face plays the wiggle at
// 1.5× speed for the first cycle (~12s window) so the visitor catches it
// immediately on landing, then settles into the slower 7s rhythm.
const VARIANT_B_FAST_MOUNT_MS = 12_000;

// Host-page overlays that should suppress the launcher. The widget is
// position:fixed and would otherwise float on top of any modal or full-screen
// nav the host site opens.
//
// `[role="dialog"]` is deliberately NOT in this list — the Benji chat window
// itself carries that role, so matching on it would make the launcher suppress
// itself. `aria-modal="true"` is the honest signal for "this blocks the page",
// and the widget window doesn't set it.
const OVERLAY_SELECTORS = ['dialog[open]', '[aria-modal="true"]'];

// Scroll-lock classes hosts put on <body> when an overlay opens. `nav-open` is
// the ccb theme's mobile nav (themes/ccb/src/js/app.js); the rest are the
// common conventions, listed so a future overlay works without a widget change.
const OVERLAY_BODY_CLASSES = ["nav-open", "modal-open", "no-scroll", "overflow-hidden"];

function anyOverlayOpen(): boolean {
  if (typeof document === "undefined") return false;

  const body = document.body;
  if (body && OVERLAY_BODY_CLASSES.some((c) => body.classList.contains(c))) return true;

  const root = document.getElementById("ccb-benji-root");
  for (const sel of OVERLAY_SELECTORS) {
    for (const el of Array.from(document.querySelectorAll(sel))) {
      // Ignore our own subtree, and elements that are in the DOM but not
      // rendered (a closed modal that stays mounted).
      if (root?.contains(el)) continue;
      if ((el as HTMLElement).getClientRects().length === 0) continue;
      return true;
    }
  }
  return false;
}

// Watches the host page for an open overlay. Returns true while one is up.
function useOverlaySuppressed(): boolean {
  const [suppressed, setSuppressed] = useState(false);

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;

    // Subtree mutations on a content page are frequent; coalesce to one check
    // per frame so we never run the query loop more than ~60×/s.
    let frame = 0;
    const check = () => {
      frame = 0;
      setSuppressed(anyOverlayOpen());
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(check);
    };

    check();
    const observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "open", "aria-modal", "style", "hidden"],
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return suppressed;
}

function Launcher({
  copy,
  interactionVariant,
  onOpen,
}: {
  copy: string;
  interactionVariant: InteractionVariant;
  onOpen: () => void;
}) {
  // Variant C state — index of the currently-displayed teaser. The bubble
  // fades out before the swap and fades in after, so it doesn't pop. Held
  // in state instead of using copy from CTA for variants A/B.
  const [teaserIdx, setTeaserIdx] = useState(0);
  const [teaserVisible, setTeaserVisible] = useState(true);
  useEffect(() => {
    if (interactionVariant !== "C") return;
    const intervalId = window.setInterval(() => {
      setTeaserVisible(false);
      window.setTimeout(() => {
        setTeaserIdx((i) => (i + 1) % VARIANT_C_TEASERS.length);
        setTeaserVisible(true);
      }, 250);
    }, 10_000);
    return () => window.clearInterval(intervalId);
  }, [interactionVariant]);

  // Variant B state — first-mount accelerated class. Cleared after ~12s so
  // the animation slows to the steady-state rhythm.
  const [isFirstMount, setIsFirstMount] = useState(interactionVariant === "B");
  useEffect(() => {
    if (interactionVariant !== "B" || !isFirstMount) return;
    const tid = window.setTimeout(() => setIsFirstMount(false), VARIANT_B_FAST_MOUNT_MS);
    return () => window.clearTimeout(tid);
  }, [interactionVariant, isFirstMount]);

  const launcherCopy =
    interactionVariant === "C" ? VARIANT_C_TEASERS[teaserIdx]! : copy;
  const bubbleClass =
    interactionVariant === "C"
      ? `ccb-bx-launcher__bubble ccb-bx-launcher__bubble--rotating${teaserVisible ? "" : " ccb-bx-launcher__bubble--rotating-hidden"}`
      : "ccb-bx-launcher__bubble";
  const btnClass = [
    "ccb-bx-launcher__btn",
    `ccb-bx-launcher__btn--interaction-${interactionVariant.toLowerCase()}`,
    interactionVariant === "B" && isFirstMount ? "ccb-bx-launcher__btn--first-mount" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const suppressed = useOverlaySuppressed();

  return (
    <div
      className={`ccb-bx-launcher${suppressed ? " ccb-bx-launcher--suppressed" : ""}`}
      data-interaction-variant={interactionVariant}
      // Hidden from AT as well as sight — a launcher behind an open modal
      // shouldn't be reachable by screen reader or tab.
      aria-hidden={suppressed || undefined}
      inert={suppressed || undefined}
    >
      <button
        type="button"
        className={bubbleClass}
        onClick={onOpen}
        aria-label={launcherCopy}
      >
        {launcherCopy}
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={onOpen}
        aria-label="Open Benji chat"
      >
        <BenjiAvatar size={45} color="var(--ccb-bx-black)" />
      </button>
    </div>
  );
}
